import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import useSiteSettings from "@/hooks/useSiteSettings";
import { siteApi } from "@/lib/api";
import { getBoardContent, stripHtml } from "@/utils/boardContent";
import { normalizeBackendRichText } from "@/utils/backendRichText";
import {
  getDisplayStatusClass,
  getDisplayStatusLabel,
  isCustomerAnswerPendingRead,
  isUnreadNoteStatus,
} from "@/utils/statusLabels";
import BoardCategoryFilter from "./BoardCategoryFilter";
import BoardSkeletonList from "./BoardSkeletonList";
import FeaturePage from "./FeaturePage.styles";
import useBoardCategoryFilter from "./useBoardCategoryFilter";

const PAGE_LIMIT = 12;

const FEATURE_COPY = {
  announcements: {
    title: "공지사항",
    eyebrow: "NOTICE",
    empty: "등록된 공지사항이 없습니다.",
    fetcher: siteApi.getAnnouncements,
  },
  events: {
    title: "이벤트",
    eyebrow: "EVENT",
    empty: "진행 중인 이벤트가 없습니다.",
    fetcher: siteApi.getEvents,
  },
  notes: {
    title: "쪽지",
    eyebrow: "MESSAGE",
    empty: "도착한 쪽지가 없습니다.",
    fetcher: siteApi.getNotes,
    authRequired: true,
  },
  customer: {
    title: "고객문의",
    eyebrow: "SUPPORT",
    empty: "등록된 문의가 없습니다.",
    fetcher: siteApi.getCustomerInquiries,
    authRequired: true,
  },
};

const normalizeRows = (payload) => {
  const data = payload?.data || payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizeTotal = (payload) => {
  const data = payload?.data || payload;
  const value = data?.count ?? data?.total ?? data?.total_count ?? data?.meta?.total;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatDate = (value, { dateOnly = false } = {}) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const options = dateOnly
    ? {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    : {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };

  return new Intl.DateTimeFormat("ko-KR", options).format(date);
};

const getBoardDateSetting = (settings = {}) =>
  settings?.set_date_format_for_events_and_notices ??
  settings?.event_notice_date_format ??
  settings?.events_and_notices_date_format ??
  settings?.show_event_notice_date ??
  settings?.show_date_for_events_and_notices ??
  settings?.display_event_notice_date ??
  settings?.is_display_event_notice_date ??
  settings?.is_show_event_notice_date;

const shouldDisplayBoardDate = (settings = {}) => {
  const setting = getBoardDateSetting(settings);
  if (setting === undefined || setting === null || setting === "") return false;
  if (typeof setting === "boolean") return setting;
  const normalized = String(setting).trim().toUpperCase();
  return !["NOT_DISPLAY", "NONE", "HIDE", "FALSE", "0", "NO", "N"].includes(normalized);
};

const formatBoardDate = (value, settings = {}) => {
  if (!shouldDisplayBoardDate(settings)) return "";
  const setting = String(getBoardDateSetting(settings) || "").trim().toUpperCase();
  return formatDate(value, {
    dateOnly: ["ONLY_DATE_DISPLAY", "DATE_ONLY", "ONLY_DATE"].includes(setting),
  });
};

const normalizeBooleanSetting = (value) => {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null || value === "") return false;
  const normalized = String(value).trim().toUpperCase();
  return ["TRUE", "1", "YES", "Y", "ON", "ENABLE", "ENABLED"].includes(normalized);
};

const getBoardImageFormatSetting = (settings = {}, mode = "events") => {
  if (mode === "announcements") {
    return (
      settings?.display_in_image_format ??
      settings?.announcement_display_in_image_format ??
      settings?.announcements_display_in_image_format ??
      settings?.notice_display_in_image_format ??
      settings?.notice_board_display_in_image_format ??
      settings?.notice_board_settings?.display_in_image_format ??
      settings?.bulletin_board_settings?.display_in_image_format
    );
  }

  return (
    settings?.event_display_in_image_format ??
    settings?.events_display_in_image_format ??
    settings?.event_board_display_in_image_format ??
    settings?.event_board_settings?.display_in_image_format
  );
};

const hasBoardImageFormatSetting = (settings = {}, mode = "events") => {
  const value = getBoardImageFormatSetting(settings, mode);
  return value !== undefined && value !== null && value !== "";
};

const isBoardImageFormatEnabled = (settings = {}, mode = "events") =>
  normalizeBooleanSetting(getBoardImageFormatSetting(settings, mode));

const getBoardSkeletonImageFormatDefault = (mode = "events") => mode === "events";

const pickDate = (row) =>
  row?.created_at || row?.current_date_time || row?.updated_at || row?.date || "";

const getRowId = (row, fallback) => row?.id || row?._id || row?.uuid || fallback;

const getTitle = (row, fallback) =>
  stripHtml(row?.title || row?.subject || row?.name || row?.message) || fallback;

const getNoteBody = (row) =>
  normalizeBackendRichText(row?.description || row?.content || row?.body || row?.message || "");

const getCustomerQuestion = (row) =>
  normalizeBackendRichText(row?.question || row?.description || row?.content || row?.message || "");

const getCustomerAnswer = (row) => normalizeBackendRichText(row?.answer || row?.reply || row?.admin_answer || "");

const isUnreadNote = (row) => isUnreadNoteStatus(row?.status);

const isUnreadCustomer = (row) => isCustomerAnswerPendingRead(row?.status);

const getStatusLabel = (row) => getDisplayStatusLabel(row?.status);

const getStatusClass = (row) => getDisplayStatusClass(row?.status);

const mergeRows = (previous, nextRows) => {
  const seen = new Set(previous.map((row, index) => String(getRowId(row, index))));
  const merged = [...previous];
  nextRows.forEach((row, index) => {
    const key = String(getRowId(row, `${merged.length}-${index}`));
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  });
  return merged;
};

const FeatureDialog = ({ dialog, isProcessing, onClose, onConfirm }) => {
  if (!dialog) return null;
  const isConfirm = dialog.variant === "confirm";

  return (
    <div className="feature-dialog-backdrop" role="alertdialog" aria-modal="true">
      <div className={`feature-dialog-panel ${dialog.type || "info"}`}>
        <strong>{dialog.title}</strong>
        <p>{dialog.message}</p>
        <div className="feature-dialog-actions">
          {isConfirm ? (
            <button type="button" className="feature-button ghost" onClick={onClose} disabled={isProcessing}>
              취소
            </button>
          ) : null}
          <button type="button" className="feature-button primary" onClick={onConfirm} disabled={isProcessing}>
            {isProcessing ? "처리 중" : dialog.confirmLabel || "확인"}
          </button>
        </div>
      </div>
    </div>
  );
};

const BoardCard = ({ row, index, config, mode, settings, isOpen, onToggle }) => {
  const imageFormatEnabled = isBoardImageFormatEnabled(settings, mode);
  const content = useMemo(
    () =>
      getBoardContent(row, `${config.title} ${index + 1}`, {
        imageFormatEnabled,
        mainImageOnly: mode === "events",
      }),
    [config.title, imageFormatEnabled, index, mode, row]
  );
  const [imageIndex, setImageIndex] = useState(0);
  const [detailImageIndex, setDetailImageIndex] = useState(0);
  const image = content.images[imageIndex] || "";
  const detailImage = content.detailImages[detailImageIndex] || "";
  const imageKey = content.images.join("|");
  const detailImageKey = content.detailImages.join("|");
  const dateText = formatBoardDate(pickDate(row), settings);
  const hasBody = Boolean(content.html);
  const showMedia = content.kind !== "text";
  const displayDetailImage = showMedia ? detailImage : "";

  useEffect(() => {
    setImageIndex(0);
    setDetailImageIndex(0);
  }, [imageKey, detailImageKey]);

  return (
    <article className={`feature-card board-card is-${content.kind} ${isOpen ? "is-open" : ""}`}>
      <button type="button" className={`board-card-main ${showMedia ? "has-media" : "no-media"}`} onClick={onToggle}>
        {showMedia ? (
          image ? (
            <span className="board-card-media">
              <img
                src={image}
                alt={content.title}
                loading={index < 4 ? "eager" : "lazy"}
                onError={() => setImageIndex((current) => current + 1)}
              />
            </span>
          ) : (
            <span className="board-card-media board-card-media-empty" aria-hidden="true">
              {config.title.slice(0, 1)}
            </span>
          )
        ) : null}

        <span className="board-card-copy">
          {dateText ? (
            <span className="board-card-kicker">
              <time>{dateText}</time>
            </span>
          ) : null}
          <strong>{content.title}</strong>
          <span className="board-card-action">{isOpen ? "닫기" : "자세히 보기"}</span>
        </span>
      </button>

      <div className="feature-card-detail" aria-hidden={!isOpen}>
        <div className="feature-card-detail-inner board-detail-inner">
          {displayDetailImage ? (
            <figure className="board-detail-image">
              <img
                src={displayDetailImage}
                alt={content.title}
                onError={() => setDetailImageIndex((current) => current + 1)}
              />
            </figure>
          ) : null}
          {hasBody ? (
            <div
              className="feature-html board-detail-html"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          ) : (
            <p className="board-no-content">표시할 상세 내용이 없습니다.</p>
          )}
        </div>
      </div>
    </article>
  );
};

const UserFeatureListPage = ({ mode = "events" }) => {
  const location = useLocation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { settings, isLoading: isSettingsLoading } = useSiteSettings();
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const sentinelRef = useRef(null);
  const scrollPanelRef = useRef(null);
  const requestIdRef = useRef(0);
  const boardSkeletonFormatRef = useRef({ mode: "", imageFormatEnabled: false });

  const actualMode = useMemo(() => {
    if (mode !== "events") return mode;
    const params = new URLSearchParams(location.search);
    return params.get("tab") === "anouncement" ? "announcements" : "events";
  }, [location.search, mode]);

  const config = FEATURE_COPY[actualMode] || FEATURE_COPY.events;
  const isNotesMode = actualMode === "notes";
  const isCustomerMode = actualMode === "customer";
  const isInboxMode = isNotesMode || isCustomerMode;
  const isCheckingAuth = Boolean(config.authRequired && (isAuthLoading || user?.isLoading));
  const {
    activeCategory: activeBoardCategory,
    buildRequestParams,
    captureCategories,
    categories: boardCategories,
    isBoardMode,
    selectCategory: selectBoardCategory,
  } = useBoardCategoryFilter(actualMode);
  const boardImageFormatEnabled = isBoardImageFormatEnabled(settings, actualMode);
  const boardHasImageFormatSetting = hasBoardImageFormatSetting(settings, actualMode);
  const boardSkeletonImageFormatSeed = boardHasImageFormatSetting
    ? boardImageFormatEnabled
    : getBoardSkeletonImageFormatDefault(actualMode);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [openRowKey, setOpenRowKey] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [isDialogProcessing, setIsDialogProcessing] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [inquiryTitle, setInquiryTitle] = useState("");
  const [inquiryQuestion, setInquiryQuestion] = useState("");

  const showAlert = useCallback((type, title, message) => {
    setDialog({ variant: "alert", type, title, message, confirmLabel: "확인" });
  }, []);

  const loadPage = useCallback(
    async (targetPage, { replace = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (replace) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const result = await config.fetcher(
        buildRequestParams(targetPage, PAGE_LIMIT)
      );

      if (requestId !== requestIdRef.current) return;

      if (result?.success) {
        const nextRows = normalizeRows(result);
        const total = normalizeTotal(result);
        captureCategories(result);
        setRows((previous) => (replace ? nextRows : mergeRows(previous, nextRows)));
        setPage(targetPage);
        setHasMore(
          total !== null ? targetPage * PAGE_LIMIT < total : nextRows.length >= PAGE_LIMIT
        );
        setErrorText("");
      } else {
        if (replace) setRows([]);
        setHasMore(false);
        setErrorText(
          config.authRequired && !isAuthenticated
            ? "로그인 후 확인할 수 있습니다."
            : "목록을 불러오지 못했습니다."
        );
      }

      setIsLoading(false);
      setIsLoadingMore(false);
    },
    [
      buildRequestParams,
      captureCategories,
      config,
      isAuthenticated,
    ]
  );

  const reloadRows = useCallback(() => {
    setOpenRowKey(null);
    return loadPage(1, { replace: true });
  }, [loadPage]);

  useEffect(() => {
    setRows([]);
    setPage(1);
    setHasMore(false);
    setOpenRowKey(null);
    setErrorText("");

    if (isCheckingAuth) {
      requestIdRef.current += 1;
      setIsLoading(true);
      setIsLoadingMore(false);
      return;
    }

    if (config.authRequired && !isAuthenticated) {
      requestIdRef.current += 1;
      setIsLoading(false);
      setIsLoadingMore(false);
      setErrorText("로그인 후 확인할 수 있습니다.");
      return;
    }

    loadPage(1, { replace: true });
  }, [actualMode, config.authRequired, isAuthenticated, isCheckingAuth, loadPage]);

  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const node = sentinelRef.current;
    const root = isInboxMode ? scrollPanelRef.current : null;
    if (!node) return undefined;
    if (isInboxMode && !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadPage(page + 1);
        }
      },
      {
        root,
        rootMargin: isInboxMode ? "90px 0px" : "180px 0px",
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isInboxMode, isLoading, isLoadingMore, loadPage, page]);

  const markRowRead = async (row, rowKey) => {
    const id = getRowId(row);
    if (!id) return;

    if (isNotesMode && isUnreadNote(row)) {
      const result = await siteApi.updateNoteStatus({ id, status: "READ" });
      if (result?.success) {
        setRows((previous) =>
          previous.map((item, index) =>
            String(getRowId(item, index)) === String(rowKey) ? { ...item, status: "READ" } : item
          )
        );
        window.dispatchEvent(new CustomEvent("notificationSync"));
      }
    }

    if (isCustomerMode && isUnreadCustomer(row)) {
      const result = await siteApi.markCustomerInquiryRead({ id });
      if (result?.success) {
        setRows((previous) =>
          previous.map((item, index) =>
            String(getRowId(item, index)) === String(rowKey)
              ? { ...item, status: "ANSWER_CHECKED" }
              : item
          )
        );
        window.dispatchEvent(new CustomEvent("notificationSync"));
      }
    }
  };

  const toggleRow = (row, index) => {
    const rowKey = String(getRowId(row, index));
    setOpenRowKey((previous) => (previous === rowKey ? null : rowKey));
    if (openRowKey !== rowKey) {
      markRowRead(row, rowKey);
    }
  };

  const runBulkAction = (action) => {
    if (!rows.length) {
      showAlert("info", "확인", "처리할 항목이 없습니다.");
      return;
    }

    if (action === "read-all") {
      setDialog({
        variant: "confirm",
        type: "info",
        title: "전체읽기",
        message: "모든 쪽지를 읽음 처리하시겠습니까?",
        confirmLabel: "전체읽기",
        onConfirm: async () => {
          const result = await siteApi.updateNoteStatus({ status: "READ" });
          if (result?.success) {
            await reloadRows();
            window.dispatchEvent(new CustomEvent("notificationSync"));
            showAlert("success", "처리 완료", result.message || "모든 쪽지를 읽음 처리했습니다.");
          } else {
            showAlert("error", "처리 실패", result?.error || "전체읽기에 실패했습니다.");
          }
        },
      });
      return;
    }

    setDialog({
      variant: "confirm",
      type: "error",
      title: "전체삭제",
      message: `${config.title} 목록을 전체 삭제하시겠습니까?`,
      confirmLabel: "전체삭제",
      onConfirm: async () => {
        const result = isNotesMode
          ? await siteApi.updateNoteStatus({ status: "DELETE" })
          : await siteApi.deleteAllCustomerInquiries();
        if (result?.success) {
          await reloadRows();
          window.dispatchEvent(new CustomEvent("notificationSync"));
          showAlert("success", "삭제 완료", result.message || "목록이 삭제되었습니다.");
        } else {
          showAlert("error", "삭제 실패", result?.error || "전체삭제에 실패했습니다.");
        }
      },
    });
  };

  const confirmDeleteRow = (row, index) => {
    const id = getRowId(row, index);
    setDialog({
      variant: "confirm",
      type: "error",
      title: "삭제 확인",
      message: "선택한 항목을 삭제하시겠습니까?",
      confirmLabel: "삭제",
      onConfirm: async () => {
        const result = isNotesMode
          ? await siteApi.updateNoteStatus({ id, status: "DELETE" })
          : await siteApi.deleteCustomerInquiry({ id });
        if (result?.success) {
          await reloadRows();
          window.dispatchEvent(new CustomEvent("notificationSync"));
          showAlert("success", "삭제 완료", result.message || "항목이 삭제되었습니다.");
        } else {
          showAlert("error", "삭제 실패", result?.error || "삭제에 실패했습니다.");
        }
      },
    });
  };

  const submitInquiry = async () => {
    const title = inquiryTitle.trim();
    const question = inquiryQuestion.trim();

    if (!title) {
      showAlert("error", "확인 필요", "문의 제목을 입력해주세요.");
      return;
    }
    if (!question) {
      showAlert("error", "확인 필요", "문의 내용을 입력해주세요.");
      return;
    }

    setIsSubmittingInquiry(true);
    const result = await siteApi.createCustomerInquiry({ title, question });
    setIsSubmittingInquiry(false);

    if (result?.success) {
      setInquiryTitle("");
      setInquiryQuestion("");
      setIsComposeOpen(false);
      await reloadRows();
      showAlert("success", "접수 완료", result.message || "고객문의가 접수되었습니다.");
    } else {
      showAlert("error", "접수 실패", result?.error || "고객문의 접수에 실패했습니다.");
    }
  };

  const closeDialog = () => {
    if (!isDialogProcessing) setDialog(null);
  };

  const confirmDialog = async () => {
    if (!dialog?.onConfirm) {
      setDialog(null);
      return;
    }
    setIsDialogProcessing(true);
    await dialog.onConfirm();
    setIsDialogProcessing(false);
  };

  const shouldShowBoardSkeleton = isBoardMode && (isLoading || isSettingsLoading);
  if (
    boardSkeletonFormatRef.current.mode !== actualMode ||
    (!shouldShowBoardSkeleton && boardHasImageFormatSetting)
  ) {
    boardSkeletonFormatRef.current = {
      mode: actualMode,
      imageFormatEnabled: boardSkeletonImageFormatSeed,
    };
  }
  const boardSkeletonImageFormatEnabled = boardSkeletonFormatRef.current.imageFormatEnabled;
  const emptyText = isLoading ? "목록을 불러오는 중입니다." : errorText || config.empty;

  return (
    <FeaturePage>
      <div className="feature-header">
        <div>
          <p className="eyebrow">{config.eyebrow}</p>
          <h1>{config.title}</h1>
        </div>
        <div className="feature-header-actions">
          {actualMode === "announcements" && <Link to="/event">이벤트 보기</Link>}
          {actualMode === "events" && <Link to="/event?tab=anouncement">공지사항 보기</Link>}
          {isNotesMode ? (
            <>
              <button type="button" className="feature-button ghost" onClick={() => runBulkAction("read-all")}>
                전체읽기
              </button>
              <button type="button" className="feature-button danger" onClick={() => runBulkAction("delete-all")}>
                전체삭제
              </button>
            </>
          ) : null}
          {isCustomerMode ? (
            <>
              <button type="button" className="feature-button primary" onClick={() => setIsComposeOpen(true)}>
                문의 작성
              </button>
              <button type="button" className="feature-button danger" onClick={() => runBulkAction("delete-all")}>
                전체삭제
              </button>
            </>
          ) : null}
        </div>
      </div>

      {isBoardMode ? (
        <BoardCategoryFilter
          activeCategory={activeBoardCategory}
          categories={boardCategories}
          onSelect={selectBoardCategory}
          title={config.title}
        />
      ) : null}

      <div
        className={`feature-scroll-panel ${isInboxMode ? "is-inbox" : ""} ${
          shouldShowBoardSkeleton ? "is-board-loading" : ""
        }`}
        ref={scrollPanelRef}
      >
        <div className={`feature-list ${isBoardMode ? "is-board-list" : ""}`}>
          {shouldShowBoardSkeleton ? (
            <BoardSkeletonList imageFormatEnabled={boardSkeletonImageFormatEnabled} />
          ) : rows.length > 0 ? (
            rows.map((row, index) => {
              const rowKey = String(getRowId(row, index));
              const isOpen = openRowKey === rowKey;
              if (isBoardMode) {
                return (
                  <BoardCard
                    key={rowKey}
                    row={row}
                    index={index}
                    config={config}
                    mode={actualMode}
                    settings={settings}
                    isOpen={isOpen}
                    onToggle={() => toggleRow(row, index)}
                  />
                );
              }

              const title = getTitle(row, `${config.title} ${index + 1}`);
              const noteBody = getNoteBody(row);
              const question = getCustomerQuestion(row);
              const answer = getCustomerAnswer(row);
              const isUnread = isNotesMode ? isUnreadNote(row) : isCustomerMode && isUnreadCustomer(row);

              return (
                <article
                  key={rowKey}
                  className={`feature-card ${isOpen ? "is-open" : ""} ${isUnread ? "is-unread" : ""}`}
                >
                  <div className="feature-card-summary">
                    <button type="button" className="feature-card-main" onClick={() => toggleRow(row, index)}>
                      <span className="feature-card-title">
                        {isUnread ? <span className="new-dot" aria-hidden="true" /> : null}
                        {title}
                      </span>
                      <span className="feature-card-meta">
                        <time>{formatDate(pickDate(row)) || "-"}</time>
                        <span className={`status-pill ${getStatusClass(row)}`}>{getStatusLabel(row)}</span>
                      </span>
                    </button>
                    {(isNotesMode || isCustomerMode) && (
                      <button
                        type="button"
                        className="feature-delete"
                        aria-label="삭제"
                        onClick={() => confirmDeleteRow(row, index)}
                      >
                        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                          <path d="M4 7h16" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M6 7l1 14h10l1-14" />
                          <path d="M9 7V4h6v3" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="feature-card-detail" aria-hidden={!isOpen}>
                    <div className="feature-card-detail-inner">
                      {isCustomerMode ? (
                        <>
                          <section>
                            <h3>문의 내용</h3>
                            <div
                              className="feature-html"
                              dangerouslySetInnerHTML={{
                                __html: question || "<p>등록된 문의 내용이 없습니다.</p>",
                              }}
                            />
                          </section>
                          <section>
                            <h3>답변</h3>
                            <div
                              className="feature-html"
                              dangerouslySetInnerHTML={{
                                __html: answer || "<p>아직 답변이 등록되지 않았습니다.</p>",
                              }}
                            />
                          </section>
                        </>
                      ) : (
                        <div
                          className="feature-html"
                          dangerouslySetInnerHTML={{
                            __html: noteBody || "<p>표시할 내용이 없습니다.</p>",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="empty-state">{emptyText}</div>
          )}
        </div>

        <div className="feature-load-sentinel" ref={sentinelRef}>
          {isLoadingMore ? "더 불러오는 중입니다." : hasMore ? "아래로 스크롤하면 더 불러옵니다." : ""}
        </div>
      </div>

      {isComposeOpen && (
        <div className="feature-compose-backdrop" role="dialog" aria-modal="true">
          <div className="feature-compose-panel">
            <div className="feature-compose-head">
              <strong>새 문의 작성</strong>
              <button type="button" aria-label="닫기" onClick={() => setIsComposeOpen(false)}>
                ×
              </button>
            </div>
            <label>
              제목
              <input
                type="text"
                value={inquiryTitle}
                onChange={(event) => setInquiryTitle(event.target.value)}
                placeholder="문의 제목"
              />
            </label>
            <label>
              내용
              <textarea
                value={inquiryQuestion}
                onChange={(event) => setInquiryQuestion(event.target.value)}
                placeholder="문의 내용을 입력해주세요."
                rows={7}
              />
            </label>
            <div className="feature-compose-actions">
              <button type="button" className="feature-button ghost" onClick={() => setIsComposeOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="feature-button primary"
                onClick={submitInquiry}
                disabled={isSubmittingInquiry}
              >
                {isSubmittingInquiry ? "접수 중" : "문의 등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      <FeatureDialog
        dialog={dialog}
        isProcessing={isDialogProcessing}
        onClose={closeDialog}
        onConfirm={confirmDialog}
      />
    </FeaturePage>
  );
};

export default UserFeatureListPage;
